import { useCallback } from 'react';
import { Res } from 'api/api.types';
import { useAxios } from '../axios';
import { CONTENT_TYPE_TEXT_PLAIN } from '../const';
import {
  AccountEntry,
  CreateAccountEntryBody,
  EditAccountEntryBody,
} from './accountEntries.types';

export const useGetInvoiceAccountEntry = () => {
  const [{ data, loading }, getInvoiceAccountEntryReq] = useAxios<{
    result: AccountEntry[];
  }>({
    method: 'GET',
  });

  const getInvoiceAccountEntry = useCallback(
    (id: number) =>
      getInvoiceAccountEntryReq({ url: `/accountentry/invoice/${id}` }),
    [getInvoiceAccountEntryReq],
  );

  return {
    data: data?.result,
    loading,
    getInvoiceAccountEntry,
  };
};

export const useRejectAccountEntry = () => {
  const [{ data, loading }, rejectAccountEntryReq] = useAxios<Res<null>>({
    method: 'PUT',
  });

  const rejectAccountEntry = useCallback(
    (id: number, reason: string) =>
      rejectAccountEntryReq({
        url: `/accountentry/${id}/reject`,
        data: reason,
        headers: CONTENT_TYPE_TEXT_PLAIN,
      }),
    [rejectAccountEntryReq],
  );

  return {
    data: data?.result,
    loading,
    rejectAccountEntry,
  };
};

export const useAcceptAccountEntry = () => {
  const [{ data, loading }, acceptAccountEntryReq] = useAxios<Res<null>>({
    method: 'PUT',
  });

  const acceptAccountEntry = useCallback(
    (accountEntriesIds: number[]) =>
      acceptAccountEntryReq({
        url: `/accountentry/accept`,
        data: accountEntriesIds,
      }),
    [acceptAccountEntryReq],
  );

  return {
    data: data?.result,
    loading,
    acceptAccountEntry,
  };
};

export const useMarkForCorrectionAccountEntry = () => {
  const [{ data, loading }, markForCorrectionAccountEntryReq] = useAxios<
    Res<null>
  >({
    method: 'PUT',
  });

  const markForCorrectionAccountEntry = useCallback(
    (id: number, reason: string) =>
      markForCorrectionAccountEntryReq({
        url: `/accountentry/${id}/markForCorrection`,
        data: reason,
        headers: CONTENT_TYPE_TEXT_PLAIN,
      }),
    [markForCorrectionAccountEntryReq],
  );

  return {
    data: data?.result,
    loading,
    markForCorrectionAccountEntry,
  };
};

export const useWithdrawCorrectionAccountEntry = () => {
  const [{ data, loading }, withdrawCorrectionAccountEntryReq] = useAxios<
    Res<null>
  >({
    method: 'PUT',
  });

  const withdrawCorrectionAccountEntry = useCallback(
    (id: number) =>
      withdrawCorrectionAccountEntryReq({
        url: `/accountentry/${id}/withdrawCorrection`,
        headers: CONTENT_TYPE_TEXT_PLAIN,
      }),
    [withdrawCorrectionAccountEntryReq],
  );

  return {
    data: data?.result,
    loading,
    withdrawCorrectionAccountEntry,
  };
};

export const useCreateAccountEntry = () => {
  const [{ data, loading }, createAccountEntryReq] = useAxios<
    Res<AccountEntry>
  >({
    method: 'POST',
  });

  const createAccountEntry = useCallback(
    (newAccountEntry: CreateAccountEntryBody) =>
      createAccountEntryReq({
        url: '/accountentry',
        data: newAccountEntry,
      }),
    [createAccountEntryReq],
  );

  return {
    createdAccountEntryData: data?.result,
    loading,
    createAccountEntry,
  };
};

export const useEditAccountEntry = () => {
  const [{ data, loading }, editAccountEntryReq] = useAxios<Res<AccountEntry>>({
    method: 'PUT',
  });

  const editAccountEntry = useCallback(
    (id: number, newAccountEntry: EditAccountEntryBody) =>
      editAccountEntryReq({
        url: `/accountentry/${id}`,
        data: newAccountEntry,
      }),
    [editAccountEntryReq],
  );

  return {
    editAccountEntryData: data?.result,
    loading,
    editAccountEntry,
  };
};

export const useAcknowledgeRejectionAccountEntry = () => {
  const [{ data, loading }, acknowledgeRejectionAccountEntryReq] = useAxios<
    Res<null>
  >({
    method: 'PUT',
  });

  const acknowledgeRejectionAccountEntry = useCallback(
    (id: number) =>
      acknowledgeRejectionAccountEntryReq({
        url: `/accountentry/${id}/acknowledgeRejection`,
      }),
    [acknowledgeRejectionAccountEntryReq],
  );

  return {
    acknowledgeRejectionAccountEntryData: data?.result,
    loading,
    acknowledgeRejectionAccountEntry,
  };
};
